import { 
  collection, 
  query, 
  getDocs, 
  where, 
  doc,
  getDoc,
  setDoc,
  DocumentData
} from 'firebase/firestore';
import { db } from '../config/firebase';
import { User, UserRole } from '../contexts/AuthContext';

const USER_CACHE_KEY = 'user_data_cache';
const CACHE_EXPIRY_MS = 5 * 60 * 1000; // 5 minutes

interface CachedUserData {
  data: User;
  timestamp: number;
}

class UserService {
  private cache = new Map<string, CachedUserData>();

  async getUserByEmail(email: string, forceRefresh = false): Promise<User | null> {
    const cacheKey = `user_${email}`;
    
    // Check cache first
    if (!forceRefresh) {
      const cached = this.cache.get(cacheKey);
      if (cached && Date.now() - cached.timestamp < CACHE_EXPIRY_MS) {
        return cached.data;
      }
    }

    try {
      // Batch query all possible fields
      const usersRef = collection(db, 'users');
      const queries = [
        query(usersRef, where('email', '==', email)),
        query(usersRef, where('username', '==', email)),
        query(usersRef, where('User_mail', '==', email))
      ];

      // Execute all queries in parallel
      const results = await Promise.all(
        queries.map(q => getDocs(q).catch(() => ({ empty: true, docs: [] })))
      );

      // Find first non-empty result
      let userData: DocumentData | null = null;
      let userDocId: string | null = null;

      for (let i = 0; i < results.length; i++) {
        const snapshot = results[i];
        if (!snapshot.empty && snapshot.docs.length > 0) {
          userDocId = snapshot.docs[0].id;
          userData = snapshot.docs[0].data();
          break;
        }
      }

      if (!userData || !userDocId) {
        throw new Error('User not found in database');
      }

      const normalizedRole = this.normalizeUserRole(userData.User_Role || userData.role || '');
      if (!normalizedRole) {
        throw new Error(`Invalid user role: ${userData.User_Role || userData.role}`);
      }

      const user: User = {
        id: userDocId,
        username: userData.Username || userData.username || email,
        role: normalizedRole,
        userGroupId: userData.user_group_id || '',
        email: userData.User_mail || userData.email || email,
      };

      // Cache the result
      this.cache.set(cacheKey, {
        data: user,
        timestamp: Date.now()
      });

      return user;
    } catch (error) {
      console.error('Error fetching user data:', error);
      throw new Error('Failed to fetch user data');
    }
  }

  private normalizeUserRole(roleValue: string): UserRole | null {
    const normalized = roleValue.toLowerCase();
    
    if (normalized === 'admin') {
      return 'admin';
    }

    if (['manager', 'sales-manager'].includes(normalized)) {
      return 'manager';
    }
    
    if (['sales-representative', 'sales', 'sales-rep', 'representative'].includes(normalized)) {
      return 'sales-representative';
    }
    
    return null;
  }

  /**
   * Get user by ID (Firestore document ID)
   */
  async getUserById(userId: string): Promise<User | null> {
    try {
      const userDocRef = doc(db, 'users', userId);
      const userDocSnap = await getDoc(userDocRef);

      if (!userDocSnap.exists()) {
        return null;
      }

      const userData = userDocSnap.data();
      const normalizedRole = this.normalizeUserRole(userData.User_Role || userData.role || '');
      if (!normalizedRole) {
        return null;
      }

      return {
        id: userDocSnap.id,
        username: userData.Username || userData.username || '',
        role: normalizedRole,
        userGroupId: userData.user_group_id || '',
        email: userData.User_mail || userData.email || '',
      };
    } catch (error) {
      console.error('Error fetching user by ID:', error);
      return null;
    }
  }

  // Batch operations for multiple users
  async getUsersByIds(userIds: string[]): Promise<User[]> {
    const batchSize = 10; // Firestore limit
    const users: User[] = [];

    for (let i = 0; i < userIds.length; i += batchSize) {
      const batch = userIds.slice(i, i + batchSize);
      const queries = batch.map(id => getDoc(doc(db, 'users', id)));
      
      const docs = await Promise.all(queries);
      docs.forEach(docSnap => {
        if (docSnap.exists()) {
          const userData = docSnap.data();
          const user: User = {
            id: docSnap.id,
            username: userData.Username || userData.username || '',
            role: this.normalizeUserRole(userData.User_Role || userData.role || '')!,
            userGroupId: userData.user_group_id || '',
            email: userData.User_mail || userData.email || '',
          };
          users.push(user);
        }
      });
    }

    return users;
  }

  /**
   * Get all users
   */
  async getAllUsers(): Promise<User[]> {
    try {
      const usersRef = collection(db, 'users');
      const snapshot = await getDocs(usersRef);
      
      const users: User[] = [];
      snapshot.docs.forEach(doc => {
        const userData = doc.data();
        const normalizedRole = this.normalizeUserRole(userData.User_Role || userData.role || '');
        if (normalizedRole) {
          users.push({
            id: doc.id,
            username: userData.Username || userData.username || '',
            role: normalizedRole,
            userGroupId: userData.user_group_id || '',
            email: userData.User_mail || userData.email || '',
          });
        }
      });
      
      return users;
    } catch (error) {
      console.error('Error fetching all users:', error);
      return [];
    }
  }

  /**
   * Get users by role
   */
  async getUsersByRole(role: UserRole): Promise<User[]> {
    try {
      const usersRef = collection(db, 'users');
      const queries = [
        query(usersRef, where('User_Role', '==', role)),
        query(usersRef, where('role', '==', role))
      ];

      const results = await Promise.all(
        queries.map(q => getDocs(q).catch(() => ({ empty: true, docs: [] })))
      );

      const users: User[] = [];
      const seenIds = new Set<string>();

      results.forEach(snapshot => {
        snapshot.docs.forEach(doc => {
          if (!seenIds.has(doc.id)) {
            seenIds.add(doc.id);
            const userData = doc.data();
            users.push({
              id: doc.id,
              username: userData.Username || userData.username || '',
              role: role,
              userGroupId: userData.user_group_id || '',
              email: userData.User_mail || userData.email || '',
            });
          }
        });
      });

      return users;
    } catch (error) {
      console.error('Error fetching users by role:', error);
      return [];
    }
  }

  clearCache(): void {
    this.cache.clear();
  }
}

// Export singleton instance for backward compatibility
export const userService = new UserService();
